import { ReactNode, useEffect, useState } from 'react';
import { EFieldType, EValueType, IField, IForm, IPage } from '../../models/form/form';
import { Box, Grid2 as Grid, Pagination, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import ILocal from '../../models/local';
import FormService from '../../services/form/FormService';
import RatingField from '../../components/form/fields/RatingField';
import IAdminPageBuildingDetail from '../../models/adminPage/adminPageBuildingDetail';
import IFloor from '../../models/floor';
import { IPropertyGroup } from '../../models/propertyGroup';
import IUser from '../../models/user';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

type FormToNode = (field: IField, form: IForm) => ReactNode;

const FilledFormPage = () => {
  const { t } = useTranslation(['common', 'form']);
  const ignoreTexts = new Set([EFieldType.Text]);
  const [imageUrls, setImageUrls] = useState<Map<string, string[]>>(new Map());
  const location = useLocation();
  const form: IForm = location.state;
  const [propertyGroup, setPropertyGroup] = useState<IPropertyGroup>();
  const [building, setBuilding] = useState<IAdminPageBuildingDetail>();
  const [floor, setFloor] = useState<IFloor>();
  const [local, setLocal] = useState<ILocal>();
  const [user, setUser] = useState<IUser>();
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<IPage>();

  useEffect(() => {
    if (!form) return;

    setCurrentPage(form.pages.find((page) => page.pageNumber === currentPageNumber));
  }, [currentPageNumber, form]);

  useEffect(() => {
    if (!form) return;

    const imageFields = form.pages.flatMap((page) =>
      page.fields.filter((field) => field.fieldType === EFieldType.Image)
    );

    fetchImageUrls(imageFields);

    setPropertyGroup(form.propertyGroup)
    setBuilding(form.building)
    setFloor(form.floor)
    setLocal(form.local)
    setUser(form.user)

    if (form.local) return;

    if (form.localId) {
      FormService.GetLocal(form.localId).then((local) => {
        setLocal(local);
      })
    }

  }, [form]);

  const fetchImageUrls = async (imageFields: IField[]) => {
    if (!imageFields || !imageFields.length) return;

    const newImageUrlsMap = new Map<string, string[]>();

    for (const imageField of imageFields) {
      if (imageField.values?.length) {
        const imageKeys = imageField.values.map((v) => v.value);
        const imageUrls = await FormService.GetImageUrls(imageKeys);
        newImageUrlsMap.set(imageField.id, imageUrls);
      }
    }

    setImageUrls(newImageUrlsMap);
  };

  const fieldTypes: Map<EFieldType, FormToNode> = new Map<EFieldType, FormToNode>([
    [
      EFieldType.Text,
      (field: IField) => (
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {field.text}
        </Typography>
      )
    ],
    [EFieldType.Input, (field: IField) => <>{field.values.map(v => <Typography> {v.value} </Typography>)}</>],
    [EFieldType.Select, (field: IField) => <>{field.values.map(v => <Typography> {v.value} </Typography>)}</>],
    [EFieldType.Image, (field: IField) => (
      <Box display="flex" marginTop={2} gap={2} flexWrap={'wrap'} justifyContent={"center"}>
        {imageUrls.get(field.id)?.map((image, index) => (
          <img key={index} src={image} alt={`Preview ${index}`} style={{ width: '200px' }} />
        ))}
      </Box>
    )],
    [EFieldType.Rating, (field: IField) => <RatingField field={field} isReadOnly></RatingField>]
  ]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
    }}>

      <Paper sx={{ borderRadius: 4, px: 3, paddingY: 3, margin: 'auto', marginTop: 4, overflowY: "scroll", minWidth: "60%", maxWidth: "100%" }}>

        <Box marginBottom={4} sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
          <Typography variant="h5" color="primary" fontWeight="medium" marginBottom={2}>
            {form && form.name}
          </Typography>

          <Grid container spacing={1}>
            <Grid size={6}>
              {propertyGroup && (
                <Typography variant="subtitle1" color="primary">{t("form:propertyGroup")}: {propertyGroup.name}</Typography>
              )}
            </Grid>
            {building && (
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary">{t("form:building")}: {building.name}</Typography>
              </Grid>
            )}
            {floor && (
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary">{t("form:floor")}: {floor.name}</Typography>
              </Grid>
            )}
            {local && (
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary">{t("form:local")}: {local.name}</Typography>
              </Grid>
            )}
            {user && (
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary">{t("form:user")}: {user.name}</Typography>
              </Grid>
            )}
            {form.date && (
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary">{t("form:formDate")}: {moment.utc(form.date).local().format("LLL")}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {form &&
          currentPage?.fields
            .sort((a, b) => a.position - b.position)
            .map((field, index) => (
              <Box marginBottom={4} key={index}>
                <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"} marginBottom={1}>
                  {field.text && !ignoreTexts.has(field.fieldType) && (
                    <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"}>
                      <Typography fontWeight='bold' display="inline">{field.text.trim()}</Typography>
                    </Box>
                  )}
                </Box>
                <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"}>{fieldTypes.has(field.fieldType) && (fieldTypes.get(field.fieldType) as Function)(field, form)}</Box>
              </Box>
            ))}

        {form.pages.length > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-evenly", mt: 8 }}>
            <Pagination
              count={form.pages.length}
              color="primary"
              onChange={(e, value) => setCurrentPageNumber(value)}
              page={currentPageNumber}
            />
          </Box>
        )}

      </Paper>
    </Box>

  );
};

export default FilledFormPage;
